import { useQueryClient } from "@tanstack/react-query";
import { useEffect } from "react";

type User = {
  id: string;
  accountId: string;
  firstName: string;
  lastName: string;
};

export class UGScriptError extends Error {
  public readonly name: string;

  constructor(message: string) {
    super(message);
    this.name = "UGScriptError";
  }
}

export default function useUserGuiding() {
  const queryClient = useQueryClient();

  useEffect(() => {
    const identifyUser = async () => {
      const { data: user } = (await queryClient.fetchQuery({
        queryKey: ["users", "me"],
      })) as { data: User };

      if (user && window.userGuiding) {
        window.userGuiding.identify(user.id, {
          name: `${user.firstName} ${user.lastName}`,
          accountId: user.accountId,
        });
      }
    };

    identifyUser();
  }, []);

  /**
   * UserGuiding Surveys have a lower z-index than our side panels and overlays.
   * To ensure they are always visible, we need to edit their z-index as soon as
   * they are added to the DOM.
   */
  useEffect(() => {
    try {
      if (!window.userGuiding || !window.userGuidingLayer) {
        throw new UGScriptError("UserGuiding script not found");
      }
      const ugContainer = document.querySelector("#userguiding");
      if (!ugContainer) {
        throw new UGScriptError("UserGuiding container element not found");
      }

      const ugObserver = new MutationObserver((mutations) => {
        for (const mutation of mutations) {
          if (mutation.type === "childList" && mutation.addedNodes.length) {
            for (const node of mutation.addedNodes) {
              if (
                node instanceof HTMLElement &&
                node.id === "ug-survey-frame"
              ) {
                node.style.zIndex = "3000";
              }
            }
          }
        }
      });
      ugObserver.observe(ugContainer, { childList: true });

      return () => {
        ugObserver.disconnect();
      };
    } catch (e: unknown) {
      if (e instanceof UGScriptError) {
        if (window.Rollbar) window.Rollbar.error(e);
      } else throw e;
    }
  }, []);
}
